import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useCallback, useState } from 'react';

import { Badge, Button, Grid, GridItem, HStack, Link, Text } from '@chakra-ui/react';

import { slugHelpers, urlHelpers } from '@nocowanie/core';
import { IconsLinear } from '@nocowanie/icons';

import { formatPhoneNumber, setCountryCode } from '@app/helpers/accommodation.helpers';
import { useDataLayerAccommodationEvents } from '@app/hooks';
import { accommodationStaticSlice, useAppSelector } from '@app/state';

export const ImportantInfoItem = ({
    name,
    fromHour,
    toHour,
    showPhoneNumber,
    children,
}: {
    name: string;
    fromHour?: string | undefined | null;
    toHour?: string | undefined | null;
    showPhoneNumber?: boolean;
    children?: React.ReactNode;
}) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'importantInfo',
    });
    const { hotelPhone } = useAppSelector(
        accommodationStaticSlice.selectors.selectContactPhoneData,
    );
    const {
        query: { id },
    } = useRouter();

    const { sendPhoneRevealedDataLayerEvent } = useDataLayerAccommodationEvents(
        slugHelpers.getIdFromSlug(id),
    );

    const [revealPhoneNumber, setRevealPhoneNumber] = useState(false);

    const handleRevealPhoneNo = useCallback(() => {
        setRevealPhoneNumber(true);
        sendPhoneRevealedDataLayerEvent();
    }, []);

    return (
        <Grid
            templateColumns={{ base: '1fr', md: '3fr 8fr' }}
            gap={{ base: 2, md: 'gutterWidthDesktop' }}
        >
            <GridItem fontWeight={'bold'}>{name}</GridItem>
            <GridItem>
                {children ? (
                    children
                ) : showPhoneNumber && hotelPhone ? (
                    <HStack gap={4}>
                        <IconsLinear.Call2
                            isRound
                            colorPalette={'gray'}
                            variant={'subtle'}
                            size={'md'}
                            boxProps={{ p: 2 }}
                        />
                        {revealPhoneNumber ? (
                            <Link fontWeight={'bold'} href={urlHelpers.getPhoneNoHref(hotelPhone)}>
                                {formatPhoneNumber(hotelPhone)}
                            </Link>
                        ) : (
                            <>
                                <Text as={'span'} fontWeight={'bold'}>
                                    {formatPhoneNumber(`${setCountryCode(hotelPhone)}XXXXXXXXX`)}
                                </Text>
                                <Button
                                    size={'sm'}
                                    ml={{ base: 'auto', md: 0 }}
                                    lineHeight={1}
                                    onClick={handleRevealPhoneNo}
                                >
                                    {t('showPhone')}
                                </Button>
                            </>
                        )}
                    </HStack>
                ) : (
                    <Badge fontSize={'md'} colorPalette={'gray'} fontWeight={'normal'}>
                        {fromHour &&
                            t('fromHour', {
                                value: fromHour,
                            })}
                        {fromHour && toHour && ' '}
                        {toHour &&
                            t('toHour', {
                                value: toHour,
                            })}
                    </Badge>
                )}
            </GridItem>
        </Grid>
    );
};
